import { Link } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    ShieldCheck,
    HeartHandshake,
    ClipboardCheck,
    FileLock2,
    Euro,
    Users,
    Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import Seo from "../components/Seo";

const HERO_IMG =
    "https://images.unsplash.com/photo-1569508926450-cd832544c4ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwY2FyZSUyMGhhbmRzfGVufDB8fHx8MTc3NjkyNjgxMHww&ixlib=rb-4.1.0&q=85";

const TRUST = [
    { icon: ShieldCheck, title: "Diskret & vertraulich", text: "Ihre Angaben bleiben geschützt und werden nicht weitergegeben." },
    { icon: ClipboardCheck, title: "Verständliche Orientierung", text: "Keine Fachsprache. Keine Paragraphenflut. Klare Einordnung." },
    { icon: HeartHandshake, title: "Persönliche Begleitung", text: "Auf Wunsch mit erfahrenem Ansprechpartner bei komplexen Fällen." },
];

const SITUATIONEN = [
    {
        title: "„Muss ich das Heim meiner Eltern bezahlen?“",
        text: "Wir ordnen die 100.000-€-Grenze nach dem Angehörigen-Entlastungsgesetz für Ihre Situation ein.",
    },
    {
        title: "„Die Rente reicht nicht mehr …“",
        text: "Klären Sie, welche Rolle Vermögen, Pflegegrad und Sozialamt jetzt spielen.",
    },
    {
        title: "„Zahlen Geschwister gleich viel?“",
        text: "Wir erklären, warum pauschale Antworten oft in die Irre führen – und worauf es wirklich ankommt.",
    },
];

const FAQ = [
    {
        q: "Muss ich das Pflegeheim meiner Eltern bezahlen?",
        a: "In vielen Fällen nicht – oder nur eingeschränkt. Nach dem Angehörigen-Entlastungsgesetz wird das Einkommen von Kindern grundsätzlich erst ab mehr als 100.000 € Jahresbrutto herangezogen. Liegen Sie darunter, muss dem Sozialamt in der Regel nichts erstattet werden. Eine individuelle Prüfung bleibt dennoch wichtig.",
    },
    {
        q: "Was bedeutet die 100.000-€-Grenze?",
        a: "Die 100.000 € beziehen sich auf das Jahresbrutto jedes einzelnen Kindes. Liegt ein Kind darüber, kann eine vertiefte Prüfung seiner Leistungsfähigkeit relevant werden. Das bedeutet nicht automatisch eine pauschale Zahlungspflicht – individuelle Faktoren spielen eine große Rolle.",
    },
    {
        q: "Wie hoch ist der Eigenanteil im Pflegeheim?",
        a: "Der Eigenanteil setzt sich aus pflegebedingtem Eigenanteil, Unterkunft, Verpflegung und Investitionskosten zusammen. Die Höhe unterscheidet sich regional erheblich. In Baden-Württemberg liegt der Gesamt-Eigenanteil häufig im oberen Bereich deutscher Bundesländer.",
    },
    {
        q: "Zahlen alle Geschwister gleich viel?",
        a: "Nein. Eine pauschale Gleichverteilung gibt es nicht. Entscheidend sind die individuellen Einkommens- und Vermögensverhältnisse, Unterhaltspflichten gegenüber eigenen Kindern und weitere Faktoren. Deshalb lohnt sich eine strukturierte Einzelfallprüfung.",
    },
    {
        q: "Ist diese Plattform eine Rechtsberatung?",
        a: "Nein. Wir liefern Orientierung, strukturieren Ihre Situation und geben Empfehlungen für nächste Schritte. Für die rechtliche Bewertung des Einzelfalls vermitteln wir den Zugang zu erfahrenen Ansprechpartnern.",
    },
];

export default function LandingPage() {
    return (
        <div>
            <Seo path="/" />
            {/* HERO */}
            <section className="brand-gradient-soft">
                <div className="container grid gap-10 py-10 sm:py-16 lg:grid-cols-2 lg:py-24">
                    <div className="flex flex-col justify-center">
                        <span
                            className="inline-flex w-fit items-center gap-2 rounded-full border border-brand-200 bg-white px-3 py-1 text-xs font-medium text-brand-700"
                            data-testid="hero-badge"
                        >
                            <Sparkles className="h-3.5 w-3.5" /> Kostenlose Ersteinschätzung in 3 Minuten
                        </span>
                        <h1 className="mt-5 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
                            Pflegeheimkosten für Angehörige{" "}
                            <span className="text-brand-600">verständlich einschätzen</span>
                        </h1>
                        <p className="mt-5 max-w-xl text-base text-ink-500 sm:text-lg">
                            Erhalten Sie in wenigen Minuten eine erste Orientierung dazu, ob Pflegeheimkosten
                            Ihrer Eltern für Sie finanziell relevant werden können – verständlich, diskret und
                            auf Wunsch mit persönlicher Begleitung.
                        </p>

                        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                            <Link to="/check" data-testid="hero-cta-primary">
                                <Button
                                    size="lg"
                                    className="h-14 w-full rounded-full bg-brand-500 px-7 text-base font-semibold hover:bg-brand-600 sm:w-auto"
                                >
                                    Jetzt kostenlos prüfen
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                            <Link to="/kontakt" data-testid="hero-cta-secondary">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="h-14 w-full rounded-full border-brand-200 px-7 text-base font-semibold text-ink-900 hover:bg-brand-50 sm:w-auto"
                                >
                                    Persönliche Analyse anfragen
                                </Button>
                            </Link>
                        </div>

                        <ul className="mt-7 flex flex-col gap-3 text-sm text-ink-700 sm:flex-row sm:flex-wrap sm:gap-x-6">
                            {TRUST.map((t) => (
                                <li key={t.title} className="flex items-start gap-2">
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-brand-500" />
                                    <span>{t.title}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="relative">
                        <div className="relative overflow-hidden rounded-3xl border border-ink-300/30 bg-white shadow-soft">
                            <img
                                src={HERO_IMG}
                                alt="Angehörige mit pflegebedürftiger Person"
                                className="aspect-[4/5] w-full object-cover"
                            />
                            <div className="absolute inset-x-4 bottom-4 rounded-2xl border border-ink-300/20 bg-white/95 p-4 shadow-softer backdrop-blur-sm">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-600">
                                    Beispielsituation
                                </div>
                                <div className="mt-1 text-sm font-medium text-ink-900">
                                    Kind verdient 150.000&nbsp;€, Eltern haben kaum Vermögen, Pflegegrad 4 –
                                    was bedeutet das konkret für Sie?
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FÜR WEN */}
            <section className="py-16 sm:py-20">
                <div className="container">
                    <div className="max-w-2xl">
                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">
                            Für wen ist das relevant?
                        </div>
                        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                            Wenn Sie sich gerade diese Fragen stellen, sind Sie richtig.
                        </h2>
                    </div>
                    <div className="mt-10 grid gap-5 md:grid-cols-3">
                        {SITUATIONEN.map((s, i) => (
                            <div
                                key={s.title}
                                className="rounded-3xl border border-ink-300/30 bg-white p-6 shadow-softer transition hover:-translate-y-1 hover:shadow-soft"
                                data-testid={`situation-${i}`}
                            >
                                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                                    {i === 0 && <Euro className="h-5 w-5" />}
                                    {i === 1 && <ClipboardCheck className="h-5 w-5" />}
                                    {i === 2 && <Users className="h-5 w-5" />}
                                </div>
                                <h3 className="font-display text-lg font-semibold text-ink-900">{s.title}</h3>
                                <p className="mt-2 text-sm text-ink-500">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SO GEHT'S */}
            <section className="bg-brand-50/50 py-16 sm:py-20">
                <div className="container">
                    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">
                                So funktioniert die Ersteinschätzung
                            </div>
                            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                                Drei Schritte. Keine Fachsprache.
                            </h2>
                        </div>
                        <Link to="/check" data-testid="howto-cta">
                            <Button className="rounded-full bg-brand-500 hover:bg-brand-600">Check starten</Button>
                        </Link>
                    </div>

                    <div className="mt-10 grid gap-5 md:grid-cols-3">
                        {[
                            { n: "01", t: "10 kurze Fragen", d: "Rolle, Einkommen, Vermögen, Pflegegrad, Region – in wenigen Minuten." },
                            { n: "02", t: "Teaser-Ergebnis", d: "Sie sehen sofort eine erste Einordnung Ihrer Situation." },
                            { n: "03", t: "Vollergebnis nach Login", d: "Kostenstruktur, konkrete Empfehlung und passendes Beratungspaket." },
                        ].map((s) => (
                            <div key={s.n} className="rounded-3xl bg-white p-7 shadow-softer">
                                <div className="font-display text-3xl font-bold text-brand-500">{s.n}</div>
                                <h3 className="mt-3 font-display text-lg font-semibold">{s.t}</h3>
                                <p className="mt-2 text-sm text-ink-500">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PAKETE TEASER */}
            <section className="py-16 sm:py-20">
                <div className="container">
                    <div className="max-w-2xl">
                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">
                            Beratungspakete
                        </div>
                        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                            Vom ersten Überblick bis zur strategischen Begleitung.
                        </h2>
                        <p className="mt-3 text-ink-500">
                            Wir empfehlen Ihnen auf Basis Ihrer Ersteinschätzung automatisch das passende Paket –
                            individuell gewählt, transparent erklärt.
                        </p>
                    </div>
                    <div className="mt-10">
                        <Link to="/pakete" data-testid="landing-pakete-cta">
                            <Button className="rounded-full bg-brand-500 hover:bg-brand-600">Pakete ansehen</Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* DOCUMENT TEASER */}
            <section className="py-10">
                <div className="container">
                    <div className="flex flex-col items-start gap-4 rounded-3xl border border-brand-200 bg-brand-50/60 p-7 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand-600">
                                <FileLock2 className="h-5 w-5" />
                            </span>
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-brand-700">
                                    Demnächst
                                </div>
                                <h3 className="font-display text-lg font-semibold">Sichere Dokumentenprüfung</h3>
                                <p className="mt-1 text-sm text-ink-500">
                                    Bald können Sie Heimverträge, Bescheide und Pflegegradunterlagen sicher
                                    hochladen und automatisch vorprüfen lassen.
                                </p>
                            </div>
                        </div>
                        <Link to="/dokumente" data-testid="doc-teaser-link">
                            <Button variant="outline" className="rounded-full border-brand-300 text-ink-900 hover:bg-white">
                                Mehr erfahren
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 sm:py-20">
                <div className="container grid gap-10 lg:grid-cols-5">
                    <div className="lg:col-span-2">
                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">FAQ</div>
                        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                            Die wichtigsten Fragen – kurz beantwortet.
                        </h2>
                        <p className="mt-3 text-ink-500">
                            Alle Antworten ersetzen keine individuelle Rechtsberatung. Für Ihre konkrete
                            Situation lohnt sich der kostenlose Check oder eine persönliche Analyse.
                        </p>
                        <Link to="/faq" className="mt-6 inline-flex items-center gap-2 text-brand-600">
                            Vollständige FAQ <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="lg:col-span-3">
                        <Accordion type="single" collapsible className="w-full" data-testid="landing-faq">
                            {FAQ.map((f, i) => (
                                <AccordionItem key={f.q} value={`item-${i}`}>
                                    <AccordionTrigger className="text-left font-display text-base font-semibold">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-sm text-ink-500">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-ink-900 py-16 text-white sm:py-20">
                <div className="container text-center">
                    <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        Jetzt kostenlos klären, ob Pflegeheimkosten für Sie relevant werden.
                    </h2>
                    <p className="mx-auto mt-3 max-w-xl text-ink-300">
                        10 Fragen. 3 Minuten. Keine Zahlung. Keine automatisierte Rechtsberatung.
                    </p>
                    <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <Link to="/check" data-testid="final-cta-check">
                            <Button
                                size="lg"
                                className="h-14 rounded-full bg-brand-500 px-7 text-base font-semibold hover:bg-brand-600"
                            >
                                Check starten <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </Link>
                        <Link to="/kontakt" data-testid="final-cta-contact">
                            <Button
                                size="lg"
                                variant="outline"
                                className="h-14 rounded-full border-white/30 bg-transparent px-7 text-base font-semibold text-white hover:bg-white/10"
                            >
                                Persönliche Analyse anfragen
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
